import React, { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Plus, Trash2, Paperclip, BadgeCheck, GraduationCap, ClipboardCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const SETTINGS = ["Domestic", "Commercial", "Industrial"];
// Suggested service authorisations — admin can type any other
const SERVICE_SUGGESTIONS = [
  "Asbestos Management Survey (HSG 264)",
  "Asbestos Refurbishment & Demolition Survey (HSG 264)",
  "Asbestos Re-inspection Survey",
  "Asbestos Bulk Sampling",
  "Asbestos Air Monitoring (HSG 248)",
  "4-Stage Clearance Analysis",
  "BOHS P402 surveyor",
  "BOHS P403 analyst",
  "BOHS P404 analyst",
  "Fire Risk Assessment (PAS 79-1)",
  "Fire Risk Assessment (PAS 79-2)",
  "Fire Door Inspection (FDIS)",
  "Compartmentation Survey",
  "Legionella Risk Assessment (BS 8580-1)",
  "Damp & Mould Survey (HHSRS)",
  "Workplace IAQ Survey",
];

export default function StaffDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);

  const load = () => api.get(`/staff/${id}/records`).then((r) => setData(r.data)).catch(() => setData({ user: null }));
  useEffect(() => { load(); }, [id]);

  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!data.user) return <div className="p-10">Staff not found. <Link to="/admin/users" className="underline">Back</Link></div>;

  const u = data.user;

  return (
    <div className="p-8 lg:p-10" data-testid="page-staff-detail">
      <Link to="/admin/users" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-2 mb-4"><ArrowLeft className="w-4 h-4" /> Users</Link>
      <div className="bg-white border border-slate-200 p-8 mb-6">
        <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Staff record</p>
        <h1 className="font-display text-3xl font-bold text-slate-900">{u.name}</h1>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-6 text-sm">
          <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Email</p><p className="text-slate-800">{u.email}</p></div>
          <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Role</p><p className="text-slate-800 capitalize">{u.role}</p></div>
          <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Job title</p><p className="text-slate-800">{u.job_title || "—"}</p></div>
          <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Phone</p><p className="text-slate-800">{u.phone || "—"}</p></div>
        </div>
      </div>

      <Section
        icon={ShieldCheck} title="Compliance authorisations"
        description="Service + setting sign-offs. A surveyor must have an active authorisation before being scheduled to that combination."
        addLabel="Sign off authorisation"
        renderRow={(a) => (
          <>
            <td className="px-4 py-3 font-medium">{a.service}</td>
            <td className="px-4 py-3">{a.setting}</td>
            <td className="px-4 py-3">{a.expires_on ? new Date(a.expires_on).toLocaleDateString() : "—"}</td>
            <td className="px-4 py-3 text-xs text-slate-500">{a.signed_off_by_name}<br/>{a.signed_off_at && new Date(a.signed_off_at).toLocaleDateString()}</td>
          </>
        )}
        columns={["Service", "Setting", "Expires", "Signed off"]}
        items={data.authorisations}
        deleteUrl={(r) => `/staff/authorisations/${r.id}`}
        onChange={load}
        FormComponent={AuthorisationForm}
        userId={id}
        formTestid="auth-form"
        testidPrefix="auth"
      />

      <Section
        icon={GraduationCap} title="Qualifications"
        description="Industry-recognised qualifications, with optional expiry tracking and certificate upload."
        addLabel="Add qualification"
        renderRow={(q) => (
          <>
            <td className="px-4 py-3 font-medium">{q.title}</td>
            <td className="px-4 py-3">{q.issuer || "—"}</td>
            <td className="px-4 py-3 text-xs">{q.reference || "—"}</td>
            <td className="px-4 py-3 text-xs">{q.awarded_on ? new Date(q.awarded_on).toLocaleDateString() : "—"}{q.expires_on && <><br/><span className="text-amber-600">exp {new Date(q.expires_on).toLocaleDateString()}</span></>}</td>
            <td className="px-4 py-3">{q.file_url ? <a href={q.file_url} target="_blank" rel="noreferrer" className="text-[#1FA59A] hover:underline"><Paperclip className="w-4 h-4 inline" /></a> : "—"}</td>
          </>
        )}
        columns={["Qualification", "Issuer", "Reference", "Dates", "File"]}
        items={data.qualifications}
        deleteUrl={(r) => `/staff/qualifications/${r.id}`}
        onChange={load}
        FormComponent={QualificationForm}
        userId={id}
        testidPrefix="qual"
      />

      <Section
        icon={ClipboardCheck} title="Training"
        description="Internal &amp; external training courses completed by this staff member."
        addLabel="Log training"
        renderRow={(t) => (
          <>
            <td className="px-4 py-3 font-medium">{t.course}</td>
            <td className="px-4 py-3">{t.provider || "—"}</td>
            <td className="px-4 py-3 text-xs">{t.completed_on ? new Date(t.completed_on).toLocaleDateString() : "—"}{t.expires_on && <><br/><span className="text-amber-600">exp {new Date(t.expires_on).toLocaleDateString()}</span></>}</td>
            <td className="px-4 py-3">{t.file_url ? <a href={t.file_url} target="_blank" rel="noreferrer" className="text-[#1FA59A] hover:underline"><Paperclip className="w-4 h-4 inline" /></a> : "—"}</td>
          </>
        )}
        columns={["Course", "Provider", "Dates", "Cert"]}
        items={data.training}
        deleteUrl={(r) => `/staff/training/${r.id}`}
        onChange={load}
        FormComponent={TrainingForm}
        userId={id}
        testidPrefix="train"
      />

      <Section
        icon={BadgeCheck} title="Observation audits"
        description="Field/site observations of the staff member's work — supporting ISO 9001 competence evidence."
        addLabel="Record observation"
        renderRow={(o) => (
          <>
            <td className="px-4 py-3 font-medium">{o.auditor_name || "—"}</td>
            <td className="px-4 py-3 text-xs">{o.audit_date ? new Date(o.audit_date).toLocaleDateString() : "—"}</td>
            <td className="px-4 py-3">{o.outcome || "—"}{o.score != null && <span className="ml-1 text-xs text-slate-500">({o.score}%)</span>}</td>
            <td className="px-4 py-3 text-xs text-slate-600 max-w-md truncate" title={o.notes}>{o.notes || "—"}</td>
            <td className="px-4 py-3">{o.file_url ? <a href={o.file_url} target="_blank" rel="noreferrer" className="text-[#1FA59A] hover:underline"><Paperclip className="w-4 h-4 inline" /></a> : "—"}</td>
          </>
        )}
        columns={["Auditor", "Date", "Outcome", "Notes", "File"]}
        items={data.observations}
        deleteUrl={(r) => `/staff/observations/${r.id}`}
        onChange={load}
        FormComponent={ObservationForm}
        userId={id}
        testidPrefix="obs"
      />
    </div>
  );
}

function Section({ icon: Icon, title, description, addLabel, columns, items, renderRow, deleteUrl, onChange, FormComponent, userId, testidPrefix }) {
  const [showForm, setShowForm] = useState(false);
  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try { await api.delete(deleteUrl({ id })); toast.success("Deleted"); onChange(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 mb-6" data-testid={`section-${testidPrefix}`}>
      <div className="flex items-start justify-between p-6 border-b border-slate-100 gap-4">
        <div className="flex items-start gap-3">
          <Icon className="w-5 h-5 text-[#1FA59A] mt-0.5" />
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900">{title}</h2>
            <p className="text-sm text-slate-500 mt-1" dangerouslySetInnerHTML={{ __html: description }} />
          </div>
        </div>
        <button onClick={() => setShowForm(true)} className="gs-gradient text-white font-semibold px-4 py-2 flex items-center gap-2 hover:opacity-90 text-sm whitespace-nowrap" data-testid={`${testidPrefix}-add-btn`}>
          <Plus className="w-4 h-4" /> {addLabel}
        </button>
      </div>
      {(items || []).length === 0 ? (
        <div className="text-sm text-slate-400 py-10 text-center">No records yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                {columns.map((c) => <th key={c} className="px-4 py-3 font-medium">{c}</th>)}
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  {renderRow(r)}
                  <td className="px-4 py-3"><button onClick={() => remove(r.id)} className="text-red-600 hover:text-red-800" data-testid={`${testidPrefix}-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showForm && <FormComponent userId={userId} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); onChange(); }} />}
    </div>
  );
}

function FileUploadInput({ value, onChange, testid }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
      toast.success(`Uploaded ${res.data.original_name}`);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <input ref={ref} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.csv" onChange={upload} className="text-xs flex-1" data-testid={testid} />
      {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      {value && <a href={value} target="_blank" rel="noreferrer" className="text-xs text-[#1FA59A] underline">view</a>}
    </div>
  );
}

function AuthorisationForm({ userId, onClose, onSaved }) {
  const [f, setF] = useState({ user_id: userId, service: "", setting: "Commercial", expires_on: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true);
    try { await api.post("/staff/authorisations", { ...f, expires_on: f.expires_on || null }); toast.success("Authorisation signed off"); onSaved(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); } finally { setBusy(false); } };
  return <ModalForm title="Sign off authorisation" onClose={onClose} onSubmit={submit} busy={busy} testid="auth-form">
    <Field label="Service / competency">
      <input list="services-list" required value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} className="ipt" data-testid="auth-service" />
      <datalist id="services-list">{SERVICE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}</datalist>
    </Field>
    <Field label="Setting"><select value={f.setting} onChange={(e) => setF({ ...f, setting: e.target.value })} className="ipt" data-testid="auth-setting">{SETTINGS.map((s) => <option key={s} value={s}>{s}</option>)}</select></Field>
    <Field label="Expires (optional)"><input type="date" value={f.expires_on} onChange={(e) => setF({ ...f, expires_on: e.target.value })} className="ipt" data-testid="auth-expires" /></Field>
    <Field label="Notes (optional)"><textarea rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="ipt" /></Field>
  </ModalForm>;
}

function QualificationForm({ userId, onClose, onSaved }) {
  const [f, setF] = useState({ user_id: userId, title: "", issuer: "", reference: "", awarded_on: "", expires_on: "", file_url: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true);
    try { await api.post("/staff/qualifications", { ...f, awarded_on: f.awarded_on || null, expires_on: f.expires_on || null }); toast.success("Qualification added"); onSaved(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); } finally { setBusy(false); } };
  return <ModalForm title="Add qualification" onClose={onClose} onSubmit={submit} busy={busy} testid="qual-form">
    <Field label="Title"><input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="ipt" data-testid="qual-title" /></Field>
    <Field label="Issuer"><input value={f.issuer} onChange={(e) => setF({ ...f, issuer: e.target.value })} className="ipt" /></Field>
    <Field label="Reference / cert number"><input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className="ipt" /></Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Awarded"><input type="date" value={f.awarded_on} onChange={(e) => setF({ ...f, awarded_on: e.target.value })} className="ipt" /></Field>
      <Field label="Expires"><input type="date" value={f.expires_on} onChange={(e) => setF({ ...f, expires_on: e.target.value })} className="ipt" /></Field>
    </div>
    <Field label="Certificate"><FileUploadInput value={f.file_url} onChange={(url) => setF({ ...f, file_url: url })} testid="qual-file" /></Field>
  </ModalForm>;
}

function TrainingForm({ userId, onClose, onSaved }) {
  const [f, setF] = useState({ user_id: userId, course: "", provider: "", completed_on: "", expires_on: "", file_url: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true);
    try { await api.post("/staff/training", { ...f, completed_on: f.completed_on || null, expires_on: f.expires_on || null }); toast.success("Training logged"); onSaved(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); } finally { setBusy(false); } };
  return <ModalForm title="Log training" onClose={onClose} onSubmit={submit} busy={busy} testid="train-form">
    <Field label="Course"><input required value={f.course} onChange={(e) => setF({ ...f, course: e.target.value })} className="ipt" data-testid="train-course" /></Field>
    <Field label="Provider"><input value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} className="ipt" /></Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Completed"><input type="date" value={f.completed_on} onChange={(e) => setF({ ...f, completed_on: e.target.value })} className="ipt" /></Field>
      <Field label="Expires"><input type="date" value={f.expires_on} onChange={(e) => setF({ ...f, expires_on: e.target.value })} className="ipt" /></Field>
    </div>
    <Field label="Certificate"><FileUploadInput value={f.file_url} onChange={(url) => setF({ ...f, file_url: url })} testid="train-file" /></Field>
  </ModalForm>;
}

function ObservationForm({ userId, onClose, onSaved }) {
  const [f, setF] = useState({ user_id: userId, auditor_name: "", audit_date: "", score: "", outcome: "Pass", file_url: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => { e.preventDefault(); setBusy(true);
    try { await api.post("/staff/observations", { ...f, audit_date: f.audit_date || null, score: f.score === "" ? null : parseInt(f.score, 10) }); toast.success("Observation recorded"); onSaved(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); } finally { setBusy(false); } };
  return <ModalForm title="Record observation audit" onClose={onClose} onSubmit={submit} busy={busy} testid="obs-form">
    <Field label="Auditor name"><input required value={f.auditor_name} onChange={(e) => setF({ ...f, auditor_name: e.target.value })} className="ipt" data-testid="obs-auditor" /></Field>
    <Field label="Audit date"><input type="date" value={f.audit_date} onChange={(e) => setF({ ...f, audit_date: e.target.value })} className="ipt" /></Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Score (%)"><input type="number" min={0} max={100} value={f.score} onChange={(e) => setF({ ...f, score: e.target.value })} className="ipt" /></Field>
      <Field label="Outcome"><select value={f.outcome} onChange={(e) => setF({ ...f, outcome: e.target.value })} className="ipt"><option>Pass</option><option>Pass with actions</option><option>Fail</option></select></Field>
    </div>
    <Field label="Notes / corrective actions"><textarea rows={4} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="ipt" /></Field>
    <Field label="Audit form / evidence"><FileUploadInput value={f.file_url} onChange={(url) => setF({ ...f, file_url: url })} testid="obs-file" /></Field>
  </ModalForm>;
}

function ModalForm({ title, onClose, onSubmit, busy, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={onClose} data-testid={testid}>
      <form onSubmit={onSubmit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg p-8 max-h-[92vh] overflow-y-auto space-y-4">
        <div className="flex justify-between items-start"><h3 className="font-display text-xl font-bold">{title}</h3><button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-900">×</button></div>
        {children}
        <button type="submit" disabled={busy} className="gs-gradient text-white font-bold px-6 py-3 w-full hover:opacity-90 disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
        <style>{`.ipt { width: 100%; border: 1px solid rgb(203, 213, 225); padding: 0.6rem 0.9rem; outline: none; font-size: 0.875rem; } .ipt:focus { border-color: #1FA59A; }`}</style>
      </form>
    </div>
  );
}
function Field({ label, children }) {
  return <div><label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2 block">{label}</label>{children}</div>;
}
